
import React, { useState } from 'react';
import type { ChurnReason } from '../types';
import CloseIcon from './icons/CloseIcon';

interface ChurnSurveyModalProps {
    actionType: 'freeze' | 'delete';
    onClose: () => void;
    onSubmit: (reason: ChurnReason) => void;
    isProcessing: boolean;
}

const reasons: { value: ChurnReason; label: string }[] = [
    { value: 'not_useful', label: "I don't find the platform useful." },
    { value: 'too_expensive', label: "The pricing is too high." },
    { value: 'difficult_to_use', label: "The interface is difficult to navigate." },
    { value: 'found_gig_elsewhere', label: "I've already found what I was looking for elsewhere." },
    { value: 'privacy_concerns', label: "Privacy or security concerns." },
    { value: 'other', label: "Other" },
];

const ChurnSurveyModal: React.FC<ChurnSurveyModalProps> = ({ actionType, onClose, onSubmit, isProcessing }) => {
    const [selectedReason, setSelectedReason] = useState<ChurnReason | null>(null);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4">
            <div className="bg-brand-surface w-full max-w-md rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center p-6 border-b border-gray-700">
                    <h3 className="text-xl font-bold text-white">
                        {actionType === 'delete' ? 'Delete Account' : 'Freeze Account'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <CloseIcon className="h-6 w-6" />
                    </button>
                </div>
                
                <div className="p-6">
                    <p className="text-gray-300 mb-6">
                        We're sorry to see you go. To help us improve, please let us know the main reason for your decision:
                    </p>
                    
                    <div className="space-y-3">
                        {reasons.map((reason) => (
                            <label 
                                key={reason.value}
                                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                    selectedReason === reason.value 
                                    ? 'border-brand-orange bg-brand-orange/10' 
                                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="churnReason"
                                    value={reason.value}
                                    checked={selectedReason === reason.value}
                                    onChange={() => setSelectedReason(reason.value)}
                                    className="h-4 w-4 text-brand-orange focus:ring-brand-orange bg-gray-700 border-gray-600"
                                />
                                <span className="ml-3 text-white font-medium">{reason.label}</span>
                            </label>
                        ))}
                    </div>

                    <div className="mt-8 flex flex-col gap-3">
                        <button
                            onClick={() => selectedReason && onSubmit(selectedReason)}
                            disabled={!selectedReason || isProcessing}
                            className={`w-full py-3 rounded-full font-bold text-white transition-opacity ${
                                actionType === 'delete' ? 'bg-red-600' : 'bg-brand-orange'
                            } disabled:opacity-50`}
                        >
                            {isProcessing ? 'Processing...' : `Confirm ${actionType === 'delete' ? 'Deletion' : 'Freezing'}`}
                        </button>
                        <button
                            onClick={onClose}
                            className="w-full py-3 rounded-full font-bold text-gray-400 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChurnSurveyModal;
